/**
 * OccupationSection — job role + employment + occupational information.
 *
 * Flutter parity: `occupation_form.dart`. 3 free-text fields, all
 * optional. The sub-object is created on first edit and cleared back to
 * null when all three fields are empty so the wire body doesn't ship a
 * blank patientOccupation.
 */

import { useFormContext, useWatch } from 'react-hook-form';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import type { PersonalDetailsFormValues } from './personal-details-schema';

export function OccupationSection() {
  const { control, setValue } = useFormContext<PersonalDetailsFormValues>();
  const jobRole = useWatch({ control, name: 'patientOccupation.jobRole' });
  const employment = useWatch({ control, name: 'patientOccupation.employment' });
  const info = useWatch({
    control,
    name: 'patientOccupation.occupationalInformation',
  });

  const update = (key: 'jobRole' | 'employment' | 'occupationalInformation', value: string) => {
    const next = value || null;
    const current = {
      jobRole: jobRole ?? null,
      employment: employment ?? null,
      occupationalInformation: info ?? null,
    };
    const merged = { ...current, [key]: next };
    const allEmpty =
      !merged.jobRole && !merged.employment && !merged.occupationalInformation;
    setValue(
      'patientOccupation',
      allEmpty
        ? null
        : {
            id: null,
            patientId: null,
            jobRole: merged.jobRole,
            employment: merged.employment,
            occupationalInformation: merged.occupationalInformation,
          },
      { shouldDirty: true },
    );
  };

  return (
    <div className="grid gap-6">
      <div>
        <FieldLabel>Job role</FieldLabel>
        <TextField
          value={jobRole ?? ''}
          onChange={(e) => update('jobRole', e.target.value)}
          placeholder="Enter job role"
          maxLength={50}
          className="mt-1" size="compact"
        />
      </div>
      <div>
        <FieldLabel>Employment</FieldLabel>
        <TextField
          value={employment ?? ''}
          onChange={(e) => update('employment', e.target.value)}
          placeholder="Enter patient employment"
          maxLength={50}
          className="mt-1" size="compact"
        />
      </div>
      <div>
        <FieldLabel>Occupational information</FieldLabel>
        <TextField
          value={info ?? ''}
          onChange={(e) => update('occupationalInformation', e.target.value)}
          placeholder="Enter info"
          maxLength={500}
          className="mt-1" size="compact"
        />
      </div>
    </div>
  );
}
